/* eslint-disable @typescript-eslint/no-explicit-any */
import { FlowRouteComponentProps, Page } from 'components/render-prop-components/FlowRoute';
import { IWithContextProps, consumer, core, safeToNumber, withContext } from '@tock/coretypes';
import { LocalDateTime } from 'js-joda';
import { LockOptions } from 'types';
import { Redirect, Route, RouteComponentProps } from 'react-router';
import { Switch } from 'react-router-dom';
import { buildPurchaseOrder, hasAnyMoniesInvolved } from 'utils/purchase';
import { logger } from '@tock/logging';
import { oc } from 'ts-optchain';
import { offeringsSelector } from 'selectors/calendarSelectors';
import { purchaseStateSelector } from 'selectors/purchaseSelectors';
import { refreshCalendar } from 'utils/refreshData';
import { stringify } from '@tock/query-string';
import { toDateTimeString } from 'utils/timeUtils';
import { tryLockAndContinue } from 'utils/lock';
import BallPulse from '@tock/shared/components/BallPulse';
import BaseComponent from '@tock/shared/components/BaseComponent';
import RescheduleConfirmRoute from 'routes/protected/account/reschedule/RescheduleConfirmRoute';
import RescheduleOptionsRoute from 'routes/protected/account/reschedule/RescheduleOptionsRoute';
import RescheduleStartRoute from 'routes/protected/account/reschedule/RescheduleStartRoute';
import _ from 'lodash';
import connect, { DefaultConnectProps, StoreType } from '@tock/shared/utils/connect';

// passed from FlowRoute component
interface RescheduleFlowRouteProps
  extends DefaultConnectProps,
    RouteComponentProps,
    IWithContextProps {
  reschedule: StoreType['reschedule'];
  lock: StoreType['lock'];
}

interface ShouldShowOptionsArg {
  reschedule: StoreType['reschedule'];
  lock: StoreType['lock'];
  config?: consumer.ConsumerConfig;
}

export const shouldShowOptions = ({ lock, reschedule, config }: ShouldShowOptionsArg): boolean => {
  const isFlex = config?.business?.isFlex;

  if (!isFlex) {
    return false;
  }
  if (!((reschedule.purchase?.ticketType?.optionGroup?.length ?? 0) > 0)) {
    return false;
  }
  if (!reschedule.purchase?.canEdit) {
    return false;
  }
  return true;
};

const pages = {
  start: {
    path: 'start',
    invalidIf: (props: RescheduleFlowRouteProps) => {
      return !props.reschedule.purchase;
    },
    shouldShow: () => true,
  },
  options: {
    path: 'options',
    invalidIf: (props: RescheduleFlowRouteProps) => {
      return !props.lock.currentLock;
    },
    shouldShow: shouldShowOptions,
  },
  confirm: {
    path: 'confirm',
    invalidIf: (props: RescheduleFlowRouteProps) => {
      return !props.reschedule.purchase || !props.lock.currentLock;
    },
    shouldShow: () => true,
  },
};

export const reschedulePageOrder: Page<RescheduleFlowRouteProps>[] = [
  pages.start,
  pages.options,
  pages.confirm,
];

interface ConnectProps extends DefaultConnectProps {
  business: StoreType['business'];
  config?: consumer.ConsumerConfig;
  calendar: StoreType['calendar'];
  lock: StoreType['lock'];
  offerings?: consumer.ConsumerOfferings;
  purchase: StoreType['purchase'];
  reschedule: StoreType['reschedule'];
  selection: StoreType['selection'];
}

type Props = RouteComponentProps & ConnectProps & IWithContextProps & FlowRouteComponentProps;

class RescheduleFlowRoute extends BaseComponent<Props> {
  componentDidMount() {
    const {
      actions,
      history,
      reschedule: { purchase, token },
    } = this.props;
    actions.navigation.set({
      title: '', // Will be overridden by specific subroutes
      hasBack: true,
    });

    if (!purchase) {
      actions.toast.showError('Cannot find reservation to reschedule.');
      logger.error('Cannot find reservation to reschedule.');
      history.replace('/profile/reservations');
      return;
    }

    this.initialize(purchase, token);
  }

  async componentDidUpdate(prevProps: Props) {
    const { history, nextRoute } = this.props;
    if (!prevProps.lock.currentLock && this.props.lock.currentLock) {
      history.push(nextRoute);
    }
  }

  componentWillUnmount() {
    const { actions } = this.props;
    actions.lock.clear();
    actions.selection.clear();
    actions.purchase.clear();
  }

  initialize(purchase: core.PastPurchase, token?: string) {
    const {
      app,
      business,
      calendar,
      actions: {
        business: businessActions,
        reschedule,
        calendar: calendarActions,
        toast,
        app: appActions,
      },
      history,
    } = this.props;
    const businessDomain = purchase.business?.domainName || undefined;

    // potentially can delete these manual calls once the Application fetch configs works after we have a :businessDomain prefixed
    if (app.config?.business?.domainName !== businessDomain && !app.configRequestInProgress) {
      // This call will trigger component reloads on mobile web views.
      // This can result in multiple conflicting loads which can cause
      //  erratic UI behavior if not properly guarded against.
      // Any changes related to this should be thoroughly tested in the app.
      appActions.fetchConfig(businessDomain);
    }

    const privateAccessToken = purchase?.ticketType?.privateAccessToken ?? undefined;
    const businessId = purchase.business?.id ?? undefined;

    if (business.business !== businessId && !business.requestInProgress) {
      businessDomain && businessActions.fetch(businessDomain);
    }

    reschedule.setRescheduleInfo(purchase, token);

    if (calendar.businessId !== businessId) {
      calendarActions.clearCalendarAndOfferings();
    }

    calendarActions.fetchV2(privateAccessToken, true, businessId, [purchase.id]);

    if (!calendar.offeringsInProgress) {
      calendarActions.fetchOfferings(privateAccessToken, true, businessId).then((response) => {
        const experience = response.payload?.experience;
        const offering = experience.find((offering: consumer.Offering) => {
          return safeToNumber(offering.id) === purchase.ticketTypeId;
        });

        if (!offering) {
          toast.showError(
            'This reservation is no longer offered, please reach out to the restaurant to make modifications.'
          );
          history.goBack();
        }
      });
    }
  }

  getBusinessScope(): ApiScope | undefined {
    const { business } = this.props;
    if (!business.business) {
      return undefined;
    }
    return { businessId: business.business.id as number };
  }

  handleUpdatePrice() {
    const {
      actions,
      app: { patron },
      calendar,
      history,
      lock,
      purchase,
      reschedule,
      selection,
    } = this.props;
    return actions.purchase
      .calculatePrice(
        buildPurchaseOrder({
          selection,
          lock,
          ownerPatron: patron || undefined,
          dinerPatron: _.head(
            purchase.calculatedPrice
              ? purchase.calculatedPrice?.dinerPatron
              : reschedule.purchase?.dinerPatron
          ),
          gratuityRate: purchase.calculatedPrice
            ? purchase.calculatedPrice?.selectedGratuityRate
            : reschedule.purchase?.selectedGratuityRate,
          promoCode: (
            _.find(
              purchase.calculatedPrice
                ? purchase.calculatedPrice?.discount
                : reschedule?.purchase?.discount,
              (discount) => discount.promoCode
            ) as core.Discount
          )?.promoCode,
          giftCardDiscounts: _.filter(
            purchase.calculatedPrice
              ? purchase.calculatedPrice?.discount
              : reschedule?.purchase?.discount,
            (discount) => !!discount.giftCard || !!discount.loyaltyProgram
          ),
          purchaseId: reschedule.purchase?.id,
        }),
        reschedule.purchase,
        this.getBusinessScope()
      )
      .catch((response: any) => {
        if (!(oc(response) as any).error.errorCode()) {
          actions.toast.showError(
            'Unable to complete your reservation, refresh your page and try again.'
          );
          return;
        }
        const errorCode = safeToNumber(response.error.errorCode);
        if (_.includes([3000, 3002, 3022], errorCode)) {
          refreshCalendar(actions, calendar.ticketTypeAccessToken).then(() => {
            history.push(`/${reschedule.purchase?.business?.domainName}`);
          });
        }
      });
  }

  handleLockExpiration() {
    const {
      actions: { lock, toast },
      history,
      reschedule,
    } = this.props;
    lock.clear();
    toast.showError('You have run out of time to book your modified reservation.');
    history.replace(`/${reschedule?.purchase?.business?.domainName}/reschedule/start`);
  }

  async handleSelectTime(
    size: number,
    dateTime: LocalDateTime,
    priceCents: number,
    options: LockOptions
  ): Promise<any> {
    const {
      actions,
      reschedule: { purchase, token: rescheduleToken },
      business: { business },
      lock,
    } = this.props;

    if (!purchase || !business) {
      throw new Error('No purchase or no business');
    }

    // Don't allow rescheduling onto the time they already have.
    const purchaseDateTime = LocalDateTime.parse(purchase.ticketDateTime);
    const editMatchesPurchaseSeatingOption = purchase.seatingAreaId === options.seatingAreaId;
    if (
      purchaseDateTime.equals(dateTime) &&
      purchase.ticketCount === size &&
      editMatchesPurchaseSeatingOption &&
      !purchase.canEdit
    ) {
      actions.toast.showError('Cannot reschedule onto a time already booked');
      return;
    }

    // Clear all existing locks upon selecting a new individual time
    if (lock.currentLock || (lock.remainingLocks?.length ?? 0) > 0) {
      actions.lock.clear();
      if (lock.currentLock) {
        await actions.lock.unlockTickets(
          {
            ticketSubsetId: lock.currentLock.ticketSubset[0].id,
            ticketGroupId: lock.currentLock.ticketSubset[0].ticketGroupId,
          },
          business.id as number
        );
      }
      // Clear remaining locks in a multi-checkout scenario
      const { remainingLocks } = this.props.lock;
      remainingLocks?.forEach((lock) => {
        this.props.actions.lock.unlockTickets({
          ticketSubsetId: lock.ticketSubset[0]?.id,
          ticketGroupId: lock.ticketSubset[0]?.ticketGroupId,
        });
      });
    }

    this.props.actions.selection.selectSize(size);
    this.props.actions.selection.selectDate(dateTime.toLocalDate().toString());
    this.props.actions.selection.selectTime(dateTime.toLocalTime().toString());

    await tryLockAndContinue(
      this.props.actions,
      purchase.ticketTypeId,
      size,
      this.props.lock,
      dateTime,
      priceCents,
      options,
      business,
      purchase.id,
      rescheduleToken
    );
  }

  handleReschedule(purchaseOrder?: core.PurchaseOrder): Promise<any> {
    const {
      actions: {
        reschedule: { reschedulePaid, reschedule: rescheduleFree },
      },
      reschedule,
      lock,
      selection,
    } = this.props;
    if (!reschedule.purchase) {
      throw new Error('reschedule action called without a purchase being rescheduled');
    }
    if (
      purchaseOrder &&
      (!!reschedule.purchase.paymentCardHold ||
        hasAnyMoniesInvolved(purchaseOrder) ||
        reschedule.purchase.purchasedOption.length > 0 ||
        purchaseOrder.optionOrder.length > 0)
    ) {
      return reschedulePaid(reschedule.purchase, purchaseOrder);
    }
    return rescheduleFree(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      reschedule.purchase!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lock.currentLock!.ticketSubset[0] as core.TicketSubset,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      toDateTimeString(selection.date!, selection.time!),
      reschedule.token
    );
  }

  handleRescheduleSuccess(purchaseOrder: core.PurchaseOrder) {
    const { reschedule, actions, history, business } = this.props;
    const { token } = reschedule;
    const purchaseId = purchaseOrder.basePurchaseId;
    history.replace({
      pathname: `/${business.business?.domainName}/receipt`,
      search: token ? stringify({ token }) : stringify({ purchaseId }),
    });
    actions.lock.clear();
    actions.reschedule.clear();
    actions.selection.clear();
  }

  renderRedirect() {
    return <Redirect to={{ ...this.props.location, pathname: `${this.props.match.path}/start` }} />;
  }

  renderStart(routeProps: RouteComponentProps<any>) {
    const {
      app,
      business,
      config,
      currentRoute,
      lock,
      nextRoute,
      offerings,
      prevRoute,
      reschedule,
      selection,
    } = this.props;
    if (!reschedule.purchase) {
      return null;
    }
    return (
      <RescheduleStartRoute
        app={app}
        business={business}
        offerings={offerings}
        lock={lock}
        onLockExpiration={this.handleLockExpiration}
        onSelectTime={this.handleSelectTime}
        purchase={reschedule.purchase}
        reschedule={reschedule}
        selection={selection}
        currentRoute={currentRoute}
        nextRoute={nextRoute}
        prevRoute={prevRoute}
        showOptions={shouldShowOptions({ lock, reschedule, config })}
        {...routeProps}
      />
    );
  }

  renderOptions(routeProps: RouteComponentProps<any>) {
    const { business, config, currentRoute, prevRoute, nextRoute, lock, reschedule } = this.props;
    if (!reschedule.purchase) {
      return null;
    }
    return (
      <RescheduleOptionsRoute
        onUpdatePrice={this.handleUpdatePrice}
        business={business.business}
        currentRoute={currentRoute}
        nextRoute={nextRoute}
        prevRoute={prevRoute}
        lock={lock}
        showOptions={shouldShowOptions({ lock, reschedule, config })}
        onLockExpiration={this.handleLockExpiration}
        {...routeProps}
      />
    );
  }

  renderConfirm(routeProps: RouteComponentProps<any>) {
    const {
      app,
      business,
      config,
      currentRoute,
      lock,
      nextRoute,
      prevRoute,
      purchase,
      reschedule,
      selection,
    } = this.props;

    if (!reschedule.purchase) {
      return null;
    }

    return (
      <RescheduleConfirmRoute
        app={app}
        business={business}
        currentRoute={currentRoute}
        isAuthenticated={!!app.jwtToken}
        isSubmitting={reschedule.requestInProgress}
        lock={lock}
        nextRoute={nextRoute}
        onLockExpiration={this.handleLockExpiration}
        onReschedule={this.handleReschedule}
        onSuccess={this.handleRescheduleSuccess}
        prevRoute={prevRoute}
        purchase={purchase}
        reschedule={reschedule}
        selection={selection}
        showOptions={shouldShowOptions({ lock, reschedule, config })}
        {...routeProps}
      />
    );
  }

  render() {
    const { app, business, calendar, match } = this.props;
    if (
      business.requestInProgress ||
      app.configRequestInProgress ||
      calendar.requestInProgress ||
      calendar.offeringsInProgress
    ) {
      return <BallPulse position="INLINE-FULL" />;
    }

    return (
      <Switch>
        <Route exact path={match.path} render={this.renderRedirect} />
        <Route path={`${match.path}/${pages.start.path}`} render={this.renderStart} />
        <Route path={`${match.path}/${pages.options.path}`} render={this.renderOptions} />
        <Route path={`${match.path}/${pages.confirm.path}`} render={this.renderConfirm} />
      </Switch>
    );
  }
}

function mapStateToProps(state: StoreType) {
  return {
    app: state.app,
    business: state.business,
    config: state.business.config,
    calendar: state.calendar,
    lock: state.lock,
    offerings: offeringsSelector(state),
    purchase: purchaseStateSelector(state),
    reschedule: state.reschedule,
    selection: state.selection,
  };
}

export default connect<RouteComponentProps<any> & FlowRouteComponentProps, ConnectProps>(
  withContext(RescheduleFlowRoute),
  mapStateToProps
);
